import { useRef, useCallback, useState, useEffect } from 'react';

// Global in-memory cache (persists across navigations)
const cache = new Map();
const STALE_TIME = 30000; // 30 seconds

export function useCachedData(key, fetcher) {
    const [data, setData] = useState(() => {
        const cached = cache.get(key);
        return cached ? cached.data : null;
    });
    const [loading, setLoading] = useState(() => !cache.has(key));
    const mountedRef = useRef(true);

    const refresh = useCallback(async () => {
        try {
            const result = await fetcher();
            const d = result?.data ?? result;
            if (mountedRef.current) {
                setData(d);
                setLoading(false);
            }
            cache.set(key, { data: d, time: Date.now() });
        } catch {
            if (mountedRef.current) setLoading(false);
        }
    }, [key, fetcher]);

    useEffect(() => {
        mountedRef.current = true;
        const cached = cache.get(key);
        if (cached) {
            setData(cached.data);
            setLoading(false);
            // Refresh in background if stale
            if (Date.now() - cached.time > STALE_TIME) refresh();
        } else {
            refresh();
        }
        return () => { mountedRef.current = false; };
    }, [key, refresh]);

    const invalidate = useCallback(() => {
        cache.delete(key);
        setLoading(true);
        refresh();
    }, [key, refresh]);

    return { data, loading, refresh, invalidate };
}

// Invalidate specific cache keys from outside
export function invalidateCache(key) {
    if (key) cache.delete(key);
    else cache.clear();
}
